/**
 * Data loading for the Company Profile page (CR-6247).
 *
 * The company and its users load independently — a Users 403 must not blank the
 * whole page, and the Overview is the tab that opens first.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { coreAPI } from '../../lib/api';
import type { Company, UserProfile } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/api/types/common';

export interface UseCompanyProfileResult {
  companyId: number;
  company: Company | null;
  loading: boolean;
  /** Set when the company fetch failed, so an empty page never reads as "no data". */
  error: string | null;
  users: UserProfile[];
  usersLoading: boolean;
  usersError: string | null;
  userSearch: string;
  setUserSearch: (search: string) => void;
  filteredUsers: UserProfile[];
  /** Re-read the company after an edit. */
  refreshCompany: () => Promise<void>;
  refreshUsers: () => Promise<void>;
}

/**
 * Load the company behind `/companies/:id` plus its user list.
 *
 * @returns company, users, their loading/error states and a user-search filter
 */
export function useCompanyProfile(): UseCompanyProfileResult {
  const { id } = useParams<{ id: string }>();
  const companyId = Number(id);

  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [users, setUsers] = useState<UserProfile[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState('');

  const refreshCompany = useCallback(async () => {
    if (!companyId || Number.isNaN(companyId)) {
      setError('No company specified');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await coreAPI.getCompany(companyId);
      setCompany(res.data);
    } catch (err: unknown) {
      console.error('Failed to load company', err);
      setError(getApiErrorMessage(err, 'Failed to load this company.'));
      setCompany(null);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const refreshUsers = useCallback(async () => {
    if (!companyId || Number.isNaN(companyId)) return;
    setUsersLoading(true);
    setUsersError(null);
    try {
      const res = await coreAPI.getCompanyUsers(companyId);
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err: unknown) {
      console.error('Failed to load company users', err);
      // Reset the list too — a stale list under an error banner would read as
      // the current membership.
      setUsers([]);
      setUsersError(getApiErrorMessage(err, 'Failed to load users for this company.'));
    } finally {
      setUsersLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void refreshCompany();
    void refreshUsers();
  }, [refreshCompany, refreshUsers]);

  const filteredUsers = useMemo(() => {
    const term = userSearch.trim().toLowerCase();
    if (!term) return users;
    return users.filter((u) => {
      const name = u.user.full_name || '';
      return (
        name.toLowerCase().includes(term)
        || u.user.email.toLowerCase().includes(term)
        || (u.job_title || '').toLowerCase().includes(term)
      );
    });
  }, [users, userSearch]);

  return {
    companyId,
    company,
    loading,
    error,
    users,
    usersLoading,
    usersError,
    userSearch,
    setUserSearch,
    filteredUsers,
    refreshCompany,
    refreshUsers,
  };
}
